import { SiteLogoCard } from "@/components/admin/SiteLogoCard";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import AdminPages from "./AdminPages";
import AdminSEO from "./AdminSEO";
import AdminUsers from "./AdminUsers";
import AdminEmailTemplates from "./AdminEmailTemplates";
import AdminCities from "./AdminCities";
import AdminRoles from "./AdminRoles";
import AdminCommissionGroups from "./AdminCommissionGroups";
import AdminEmailSending from "./AdminEmailSending";
import { NotifyRecipientsCard } from "@/components/shared/NotifyRecipientsCard";
import { FileText, Globe, UserCog, Mail, Building2, ShieldCheck, Coins, Send } from "lucide-react";
import { SETTINGS_TAB_AREAS, useAdminAccess } from "@/lib/adminAccess";

const settingsTabs = [
  { key: "pages", label: "Pages", icon: FileText },
  { key: "cities", label: "City Pages", icon: Building2 },
  { key: "seo", label: "SEO", icon: Globe },
  { key: "emails", label: "Email Templates", icon: Mail },
  { key: "email-sending", label: "Email Sending", icon: Send },
  { key: "commissions", label: "Commission Groups", icon: Coins },
  { key: "users", label: "Users", icon: UserCog },
  { key: "roles", label: "User Roles", icon: ShieldCheck },
];

export default function AdminSettings() {
  const { can } = useAdminAccess();
  const tabs = settingsTabs.filter((t) => can(SETTINGS_TAB_AREAS[t.key]));
  const [params] = useSearchParams();
  const [picked, setActiveTab] = useState(params.get("tab"));
  const activeTab = tabs.some((t) => t.key === picked) ? picked : tabs[0]?.key;

  return (
    <AdminLayout title="Settings" breadcrumb="Settings">
      <div className="space-y-6" data-testid="admin-settings">
        {/* Tab selector */}
        <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="settings-tabs">
          {tabs.length === 0 && <p className="text-sm text-slate-500 px-3 py-2">You don't have access to any settings.</p>}
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-body font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-navy text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`settings-tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "pages" && <><SiteLogoCard /><AdminPages embedded /></>}
        {activeTab === "cities" && <AdminCities embedded />}
        {activeTab === "seo" && <AdminSEO embedded />}
        {activeTab === "emails" && <><NotifyRecipientsCard /><AdminEmailTemplates embedded /></>}
        {activeTab === "email-sending" && <AdminEmailSending />}
        {activeTab === "commissions" && <AdminCommissionGroups />}
        {activeTab === "users" && <AdminUsers embedded />}
        {activeTab === "roles" && <AdminRoles />}
      </div>
    </AdminLayout>
  );
}
